import { AnyObject } from '@common/utils';
import { flatMap, isEmpty } from 'lodash';
import shortUUID from 'short-uuid';
import {
  ActivityType,
  AIFeature,
  EmailTemplateType,
  MarketRequestType,
  PromptResultFeature,
  PromptResultFeatureType,
  SubmissionEmailLabel,
  SubmissionMarketRequestStatus,
} from 'enums';
import {
  useCurrentUser,
  useGenerateMarketingEmailContent,
  useSearchTeamInsuranceProduct,
  useSearchUserMarket,
} from 'hooks';
import useRetrieveExtendedSubmission from 'hooks/api/extended-submission/useRetrieveExtendedSubmission';
import { messages } from 'i18n';
import { Layer, PartialSubmission } from 'types';
import { EmailEditorProps } from 'broker/components/EmailEditor/store/EmailEditorStoreProvider';
import { PostEmailInformation } from 'broker/components/EmailEditor/types';
import {
  convertEndUserToRecipientGroup,
  convertSubmissionContactsToRecipientGroup,
  createRecipientGroupFromSubmissionCustomer,
} from 'broker/components/Emails/recipient-utils';
import { allExpectedSubmissionFileTypes } from 'broker/config/submission-tasks-file-types-config';
import { EmailEditorPageMode } from 'broker/enums';
import { CreateThreadAction } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/SingleAIFeatureContextProvider';
import { useGetMarketingEmailAttachments } from 'broker/pages/SubmissionWorkspacePage/hooks/useGetMarketingEmailAttachments';
import useSubmissionMissingFiles from 'broker/pages/SubmissionWorkspacePage/hooks/useSubmissionMissingFiles';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { isPrimaryCoverageLayer } from 'broker/utils';
import { EmailEditorPageLocationState } from './types';
import { useHandleRemarkingUpdateEmailSent } from './useHandleRemarkingUpdateEmailSent';
import { useHandleSolicitationEmail } from './useHandleSolicitationEmail';
import { getQuotesFileIds, sendRfqAiContentDecorator } from './utils';

interface GetEmailEditorPageProps extends Partial<EmailEditorProps> {
  title: string;
  subtitle?: string;
  isLoading?: boolean;
  aiFeatureProps?: {
    feature: AIFeature;
    popupTitle: string;
    createThread: CreateThreadAction;
    contentDecorator: (generatedContent: string) => string;
    subject: string;
    promptResultFeature: {
      type: PromptResultFeatureType;
      input: AnyObject;
    };
  };
}

function useHandleEmailSentToMarket() {
  const { logEmailActivities, updateSubmissionMarketRequest } = useSubmissionsWorkspace();

  return async (
    postEmailInformation: PostEmailInformation,
    layer?: Layer,
    locationState?: EmailEditorPageLocationState,
  ) => {
    const { submissionMarketIdToEmailId, emailTemplateType } = postEmailInformation;

    await Promise.all([
      locationState?.updateSubmissionMarketRequestStatus
        ? updateSubmissionMarketRequest(locationState.updateSubmissionMarketRequestStatus.submissionMarketRequestId, {
            status: locationState.updateSubmissionMarketRequestStatus.targetStatus,
          })
        : undefined,
      logEmailActivities(
        emailTemplateType,
        Object.keys(submissionMarketIdToEmailId).map((submissionMarketId) => ({
          submissionMarketId,
          submissionEmailId: submissionMarketIdToEmailId[submissionMarketId],
          layerId: layer?.id,
        })),
        false,
      ),
    ]);
  };
}

export default function useGetEmailEditorProps(
  state: EmailEditorPageLocationState,
  submission?: PartialSubmission,
): GetEmailEditorPageProps {
  const handleEmailSentToMarket = useHandleEmailSentToMarket();
  const handleEmailSentToRemarkingUpdate = useHandleRemarkingUpdateEmailSent();
  const {
    preSelectedFileIds: solicitationPreSelectedFileIds,
    solicitationRecipients,
    handleSolicitationEmailSent,
  } = useHandleSolicitationEmail();
  const { me } = useCurrentUser();
  const { marketingEmailAttachments, isLoading: loadingMarketingEmailAttachments } = useGetMarketingEmailAttachments();
  const { generateMarketingEmailContent } = useGenerateMarketingEmailContent();
  const { items: teamProducts } = useSearchTeamInsuranceProduct();
  const { items: userMarkets } = useSearchUserMarket();
  const { refetch: refetchExtendedSubmission } = useRetrieveExtendedSubmission({ submissionId: submission!.id });

  const { logSubmissionActivity, quotes, updateQuote, addSubmissionMarkets, updateSubmissionMarketRequest } =
    useSubmissionsWorkspace();
  const { mode, layer, recipientGroups, marketRequestType, triggeringQuoteId } = state;
  const triggeringQuote = triggeringQuoteId
    ? quotes.find((quoteItem) => quoteItem.id === triggeringQuoteId)
    : undefined;
  // Optimization: prevent the box query from running if the template is not MissingInformationRequest
  const { getMissingFileTypes, isLoading: loadingMissingFileTypes } = useSubmissionMissingFiles(
    mode === EmailEditorPageMode.MissingInformationRequest,
  );
  const isLoading = loadingMarketingEmailAttachments || loadingMissingFileTypes;

  const emailEditorPropsByMode: Record<EmailEditorPageMode, GetEmailEditorPageProps> = {
    [EmailEditorPageMode.BorAcknowledgment]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.borAcknowledgment.title,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedTemplateType: EmailTemplateType.BorAcknowledgment,
      postEmailsSent: async () => {
        const groupKey = shortUUID().new();

        if (!state.selectedTeamProductIds) {
          return;
        }

        const selectedTeamProducts = teamProducts.filter((teamProduct) =>
          state.selectedTeamProductIds?.includes(teamProduct.id),
        );

        await addSubmissionMarkets(
          selectedTeamProducts.map((teamProduct) => {
            const userMarket = userMarkets.find((item) => item.marketId === teamProduct.insuranceProduct.marketId);
            if (!userMarket) {
              throw new Error(`User market not found for marketId: ${teamProduct.insuranceProduct.marketId}`);
            }
            return {
              userMarketId: userMarket.id,
              contactIds: teamProduct.contacts?.map((contact) => contact.id) || [],
              marketRequestType: isPrimaryCoverageLayer(teamProduct.insuranceProduct.coverageLayer)
                ? MarketRequestType.Primary
                : MarketRequestType.Excess,
              activityLogGroupKey: groupKey,
              insuranceProductId: teamProduct.insuranceProduct.id,
            };
          }),
          false,
        );

        const res = await refetchExtendedSubmission();

        await Promise.all(
          flatMap(res.data?.markets, (market) =>
            market.marketRequests.map((marketRequest) =>
              updateSubmissionMarketRequest(marketRequest.id, {
                status: SubmissionMarketRequestStatus.AwaitingPolicy,
              }),
            ),
          ),
        );
      },
    },
    [EmailEditorPageMode.SendRfq]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.submitToMarkets.title,
      subtitle: messages.submissionWorkspace.dialogs.emailEditor.submitToMarkets.subtitle,
      preSelectedRecipients: recipientGroups || [],
      preSelectedFileIds: marketingEmailAttachments,
      postEmailsSent: async (postEmailInformation) => handleEmailSentToMarket(postEmailInformation, layer),
      emailLabel: SubmissionEmailLabel.SubmissionMarketing,
      templateContextMetaData: {
        layer,
      },
      aiFeatureProps: {
        feature: AIFeature.MarketingEmailGeneration,
        popupTitle: messages.submissionWorkspace.dialogs.emailEditor.submitToMarkets.aiPopupTitle,
        createThread: () => generateMarketingEmailContent({ submissionId: submission!.id }),
        contentDecorator: sendRfqAiContentDecorator,
        subject: '@COVERAGE-LINES Submission - @INSURED-NAME - Need by @NEED-BY-DATE',
        promptResultFeature: {
          type: PromptResultFeature.MarketingEmail,
          input: { submissionId: submission!.id },
        },
      },
      marketRequestType,
    },

    [EmailEditorPageMode.SendCustomer]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToCustomers,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      postEmailsSent: async () => logSubmissionActivity({ activityType: ActivityType.CustomerEmailSent }),
      preSelectedFileIds: state.attachedFileIds ? state.attachedFileIds : [],
      preSelectedTemplateType: EmailTemplateType.Compose,
    },
    [EmailEditorPageMode.SendInsured]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToInsured,
      preSelectedRecipients: submission ? [convertSubmissionContactsToRecipientGroup(submission)] : [],
      postEmailsSent: async () => logSubmissionActivity({ activityType: ActivityType.InsuredEmailSent }),
      preSelectedFileIds: state.attachedFileIds ? state.attachedFileIds : [],
      preSelectedTemplateType: EmailTemplateType.Compose,
      bccEmailsInit: submission?.user?.email ? [submission.user.email] : [],
    },
    [EmailEditorPageMode.CannotPursue]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.canNotPursue,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedTemplateType: EmailTemplateType.CannotPursue,
    },
    [EmailEditorPageMode.PositiveExpectation]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.positiveExpectation,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedTemplateType: EmailTemplateType.PositiveExpectation,
    },
    [EmailEditorPageMode.MissingInformationRequest]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.missingInformationRequest.title,
      subtitle: messages.submissionWorkspace.dialogs.emailEditor.missingInformationRequest.subtitle,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedTemplateType: EmailTemplateType.MissingInformationRequest,
      templateContextMetaData: {
        submissionMissingFiles: getMissingFileTypes(allExpectedSubmissionFileTypes),
      },
    },
    [EmailEditorPageMode.UserNotification]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessage,
      preSelectedRecipients: me?.endUser ? [convertEndUserToRecipientGroup(me.endUser)] : [],
      preSelectedTemplateType: EmailTemplateType.UserNotification,
    },
    [EmailEditorPageMode.SendMarket]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToMarkets,
      preSelectedRecipients: recipientGroups || [],
      postEmailsSent: async (postEmailInformation) => handleEmailSentToMarket(postEmailInformation, undefined, state),
      preSelectedFileIds: state.attachedFileIds ? state.attachedFileIds : [],
      preSelectedTemplateType: EmailTemplateType.Compose,
    },
    [EmailEditorPageMode.EscalationPolicy]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessage,
      preSelectedRecipients: me?.endUser ? [convertEndUserToRecipientGroup(me.endUser)] : [],
      preSelectedTemplateType: EmailTemplateType.EscalationPolicy,
    },
    // This special mode is used for the Reply email editor, title is usually overridden by useReplyEmail
    [EmailEditorPageMode.Default]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessage,
    },
    [EmailEditorPageMode.NewQuote]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToCustomers,
      preSelectedTemplateType: EmailTemplateType.NewQuote,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedFileIds: triggeringQuote?.capitolaQuoteFileIds ?? [],
      templateContextMetaData: {
        triggeringQuote,
      },
      postEmailsSent: async () => {
        if (triggeringQuote) {
          const { sharingDetails } = triggeringQuote;
          await updateQuote(triggeringQuote.id, { sharingDetails: { ...sharingDetails, quoteShared: true } });
        }
      },
    },
    [EmailEditorPageMode.LayerBinder]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToCustomers,
      preSelectedTemplateType: EmailTemplateType.LayerBinder,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedFileIds: !isEmpty(triggeringQuote?.flowBinderFileIds)
        ? triggeringQuote?.flowBinderFileIds
        : triggeringQuote?.binderFileIds,
      templateContextMetaData: {
        triggeringQuote,
      },
      postEmailsSent: async () => {
        if (triggeringQuote) {
          const { sharingDetails } = triggeringQuote;
          await updateQuote(triggeringQuote.id, { sharingDetails: { ...sharingDetails, binderShared: true } });
        }
      },
    },
    [EmailEditorPageMode.LayerPolicy]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToCustomers,
      preSelectedTemplateType: EmailTemplateType.LayerPolicy,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedFileIds: triggeringQuote?.policyFileIds ?? [],
      templateContextMetaData: {
        triggeringQuote,
      },
      postEmailsSent: async () => {
        if (triggeringQuote) {
          const { sharingDetails } = triggeringQuote;
          await updateQuote(triggeringQuote.id, { sharingDetails: { ...sharingDetails, policyShared: true } });
        }
      },
    },
    [EmailEditorPageMode.MarketingReport]: {
      title: messages.marketingReportPage.composeMarketingReport,
      preSelectedTemplateType: EmailTemplateType.MarketingReport,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      preSelectedFileIds: getQuotesFileIds(quotes),
      postEmailsSent: () =>
        logSubmissionActivity({
          activityType: ActivityType.MarketingReportSent,
        }),
    },
    [EmailEditorPageMode.InformationRequestsReport]: {
      title: messages.marketingReportPage.composeMarketingReport,
      preSelectedTemplateType: EmailTemplateType.InformationRequestedRetailer,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      postEmailsSent: () =>
        logSubmissionActivity({
          activityType: ActivityType.InformationRequestSent,
        }),
    },
    [EmailEditorPageMode.ConceptualTower]: {
      title: messages.ConceptualTowerPage.sendMode.title,
      subtitle: messages.ConceptualTowerPage.sendMode.subtitle,
      preSelectedTemplateType: EmailTemplateType.ConceptualTower,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
    },
    [EmailEditorPageMode.RemarketingUpdate]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendMessageToCustomers,
      preSelectedRecipients: submission ? [createRecipientGroupFromSubmissionCustomer(submission)] : [],
      postEmailsSent: async () => handleEmailSentToRemarkingUpdate(),
      preSelectedTemplateType: EmailTemplateType.RemarketingUpdate,
    },
    [EmailEditorPageMode.SendSolicitation]: {
      title: messages.submissionWorkspace.dialogs.emailEditor.sendSolicitation,
      preSelectedRecipients: solicitationRecipients.preSelectedRecipients,
      ccEmailsInit: solicitationRecipients.ccEmailsInit,
      bccEmailsInit: solicitationRecipients.bccEmailsInit,
      preSelectedTemplateType: EmailTemplateType.RenewalInformationSolicitation,
      preSelectedFileIds: solicitationPreSelectedFileIds,
      postEmailsSent: async (postEmailInformation) => handleSolicitationEmailSent(postEmailInformation),
    },
  };

  return { ...emailEditorPropsByMode[mode], isLoading };
}
